import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageContainer } from '@/components/layout';
import { Button, Card, CardContent, ErrorBanner, PageHeader, PageLoading } from '@/components/ui';
import { TeamOverviewGrid } from '@/components/teams/TeamOverviewGrid';
import { TeamDetail } from '@/components/teams/TeamDetail';
import { TeamFilterBar } from '@/components/teams/TeamFilterBar';
import {
  filterTeamsByRegion,
  filterTeamsByRole,
  filterTreeBySearch,
  groupTeamsIntoRegionSections,
  listAvailableRegions,
  listAvailableRoles,
  sortTeamsByName,
  type TeamSortDirection,
} from '@/components/admin/team-role-tree-helpers';
import { teamVisualizationApi } from '@/services/team-visualization-api';
import { getErrorMessage } from '@/lib/utils';
import type { ITeamMembershipTreeTeam, IPublicTeamTreeResponse } from '@skystone/shared';

/**
 * Public Team Visualization page (PRODOPSAI-528/530/531/532). Read-only, all
 * authenticated users — the route is bare (no RoleBasedRoute). Loads the full
 * team tree once and renders either the filtered overview grid or, when
 * `?team=<id>` is set, that team's role-swimlane detail. Search / region / role
 * filters and the A–Z sort are URL-backed (`q`, `region`, `role`, `sort`).
 */
export function TeamVisualizationPage() {
  const [data, setData] = useState<IPublicTeamTreeResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedId = searchParams.get('team');
  const search = searchParams.get('q') ?? '';
  const region = searchParams.get('region') ?? '';
  const role = searchParams.get('role') ?? '';
  const sort: TeamSortDirection = searchParams.get('sort') === 'desc' ? 'desc' : 'asc';

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setData(await teamVisualizationApi.getTeamOverview());
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load teams'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  // `load` handles its own errors and never rejects; the `.catch` is only to
  // satisfy no-floating-promises without the `void` operator (SonarCloud S3735).
  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  const updateParam = useCallback(
    (key: string, value: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const key of ['q', 'region', 'role', 'sort']) next.delete(key);
      return next;
    });
  }, [setSearchParams]);

  const teams: ITeamMembershipTreeTeam[] = useMemo(() => data?.teams ?? [], [data]);
  const regionOptions = useMemo(() => listAvailableRegions(teams), [teams]);
  const roleOptions = useMemo(() => listAvailableRoles(teams), [teams]);

  const visibleTeams = useMemo(() => {
    const bySearch = filterTreeBySearch(teams, search);
    const byRegion = filterTeamsByRegion(bySearch, region);
    const byRole = filterTeamsByRole(byRegion, role);
    return sortTeamsByName(byRole, sort);
  }, [teams, search, region, role, sort]);

  const regionSections = useMemo(
    () => groupTeamsIntoRegionSections(visibleTeams),
    [visibleTeams],
  );

  const hasActiveFilters =
    search !== '' || region !== '' || role !== '' || sort !== 'asc';

  let content: ReactNode;
  if (isLoading) {
    content = <PageLoading />;
  } else if (error !== null) {
    content = (
      <div className="space-y-3">
        <ErrorBanner message={error} />
        <Button variant="outline" onClick={() => load().catch(() => undefined)}>
          Try again
        </Button>
      </div>
    );
  } else if (data === null || data.teams.length === 0) {
    content = (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          No teams to show yet.
        </CardContent>
      </Card>
    );
  } else {
    const selectedTeam =
      selectedId === null ? null : data.teams.find((t) => t.team.id === selectedId) ?? null;
    if (selectedTeam === null) {
      content = (
        <div className="space-y-4">
          <TeamFilterBar
            search={search}
            onSearchChange={(v) => updateParam('q', v)}
            regions={regionOptions}
            region={region}
            onRegionChange={(v) => updateParam('region', v)}
            roles={roleOptions}
            role={role}
            onRoleChange={(v) => updateParam('role', v)}
            sort={sort}
            onSortChange={(v) => updateParam('sort', v === 'asc' ? '' : v)}
            hasActiveFilters={hasActiveFilters}
            onClear={clearFilters}
          />
          {visibleTeams.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No teams match your filters.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-8" data-testid="region-sections">
              {regionSections.map((section) => (
                <section key={section.region} aria-label={section.label}>
                  <div className="mb-3 flex items-baseline justify-between border-b border-border pb-2">
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-foreground">
                      {section.label}
                    </h2>
                    <span className="text-xs text-muted-foreground">
                      {section.teams.length === 1 ? '1 team' : `${section.teams.length} teams`}
                    </span>
                  </div>
                  <TeamOverviewGrid
                    teams={section.teams}
                    onSelectTeam={(id) => updateParam('team', id)}
                  />
                </section>
              ))}
            </div>
          )}
        </div>
      );
    } else {
      content = <TeamDetail team={selectedTeam} onBack={() => updateParam('team', '')} />;
    }
  }

  return (
    <PageContainer variant="wide" className="space-y-6">
      <PageHeader title="Teams" subtitle="Browse all teams and their members" />
      {content}
    </PageContainer>
  );
}
